import type ChooseUseCaseForm from "./ChooseUseCaseForm";
import type { GraphqlMappedError } from "@/services/graphqlErrorService";

import { useState } from "react";

import RenderOnce from "@/components/ui/renderOnce";
import useDelayedModal from "@/hooks/useDelayedModal";
import { notificationService } from "@/services/notificationService";
import { withSuspense } from "@/utils/hoc/withSuspense";

import { CATEGORY_TYPES } from "./constants";
import useChooseUseCase from "./hooks/useChooseUseCase";
import useGetChooseUseCaseModal from "./hooks/useGetChooseUseCaseModal";

type TChooseUseCaseOnSubmit = Parameters<
  typeof ChooseUseCaseForm
>[0]["onSubmit"];

const BaseChooseUseCaseModalLazy = withSuspense(
  () => import("./BaseChooseUseCaseModal")
);

function ChooseUseCaseModal() {
  const [isSuccess, setIsSuccess] = useState(false);
  const chooseUseCaseModal = useGetChooseUseCaseModal();
  const { show, hide } = useDelayedModal(chooseUseCaseModal.data.isShow);

  const handleChooseUseCaseError = (e: GraphqlMappedError) => {
    notificationService.danger({
      msg: e.errorTranslation,
    });
  };

  const chooseUseCase = useChooseUseCase({
    onError: handleChooseUseCaseError,
  });

  const handleCancel = () => {
    if (chooseUseCase.loading) return;

    if (!isSuccess) {
      chooseUseCase.submit({ category: CATEGORY_TYPES.UNKNOWN });
    }

    hide();
  };

  const handleChooseUseCase: TChooseUseCaseOnSubmit = async (values) => {
    try {
      await chooseUseCase.submit(values);

      setIsSuccess(true);
    } catch {
      hide();
    }
  };

  return (
    <RenderOnce when={show}>
      <BaseChooseUseCaseModalLazy
        isSuccess={isSuccess}
        isLoading={chooseUseCase.loading}
        show={show}
        onHide={handleCancel}
        onSubmit={handleChooseUseCase}
      />
    </RenderOnce>
  );
}

export default ChooseUseCaseModal;
